import { useEffect } from "react";
import { X, Mic, Headphones, FileText, Clock, Type, CheckCircle2, Sparkles } from "lucide-react";
import type { CaseDetail } from "./types";
import { ScoreRing, classColor, classLabel, C } from "./Charts";

type Props = {
  detail: CaseDetail;
  setupMin: number;
  typingWpm: number;
  reviewMin: number;
  relistenSpeed: number;
  onClose: () => void;
};

function formatMin(min: number) {
  if (min < 60) return `${Math.round(min)} min`;
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h}h ${m}m`;
}

function classIcon(c: CaseDetail["class"]) {
  if (c === "narrative") return <Mic size={14} />;
  if (c === "interaction" || c === "mixed") return <Headphones size={14} />;
  return <FileText size={14} />;
}

export function CaseViewer({ detail, setupMin, typingWpm, reviewMin, relistenSpeed, onClose }: Props) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const typingMin = detail.words / typingWpm;
  const relistenMin = detail.interaction_sec / 60 / relistenSpeed;
  const dictationMin = detail.narrative_sec / 60;

  const interactedWithSuggestions = !!(
    detail.has_implemented_suggestion || detail.has_suggestion_insert
  );
  const showTypedBanner =
    detail.class === "manual_typed" && !interactedWithSuggestions;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            {detail.score != null ? (
              <ScoreRing score={detail.score} size={64} label="Quality" />
            ) : (
              <div className="w-16 h-20 flex items-center justify-center text-slate-300">
                <FileText size={28} />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Case</div>
              <div className="text-lg font-semibold text-slate-900 truncate">
                {detail.case_number || `Case ${detail.id.slice(0, 8)}`}
              </div>
              <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
                <span>{detail.officer_name}</span>
                {detail.badge_id && <span>#{detail.badge_id}</span>}
                <span className="text-slate-300">·</span>
                <span>
                  {new Date(detail.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span
                  className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: classColor(detail.class) + "20",
                    color: classColor(detail.class),
                  }}
                >
                  {classIcon(detail.class)}
                  {classLabel(detail.class)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-100 text-slate-600"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          {showTypedBanner ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
              <div className="flex items-center gap-2 text-slate-600 mb-1">
                <FileText size={18} />
                <span className="text-xs uppercase tracking-wider font-semibold">
                  Typed manually
                </span>
              </div>
              <div className="text-sm text-slate-700">
                This report was written by hand without recorded AI assistance, so
                no time-savings are credited. The system may still have generated
                suggestions for the officer to review in the editor — not all
                officers explicitly check those off as they work through a report,
                so some AI engagement may not be reflected here.
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
                <div className="flex items-center gap-2 text-emerald-700 mb-1">
                  <CheckCircle2 size={18} />
                  <span className="text-xs uppercase tracking-wider font-semibold">Estimated time saved</span>
                </div>
                <div className="text-3xl font-bold text-emerald-700 tabular-nums">
                  {formatMin(detail.saved_min)}
                </div>
                <div className="text-sm text-emerald-700/80 mt-1">
                  vs. an estimated{" "}
                  <span className="font-semibold">{formatMin(detail.manual_min)}</span> to write this report
                  by hand and re-listen to interaction audio.
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
                  How we got there
                </div>
                <div className="space-y-2">
                  <CalcRow
                    icon={<FileText size={14} />}
                    color="#0891b2"
                    label="Per-report setup eliminated"
                    detail={`Pulling up case, running subjects, gathering evidence, formatting (${setupMin} min flat)`}
                    value={`+${setupMin} min`}
                  />
                  <CalcRow
                    icon={<Type size={14} />}
                    color={C.primary}
                    label="Typing eliminated"
                    detail={`${detail.words.toLocaleString()} words ÷ ${typingWpm} wpm`}
                    value={`+${formatMin(typingMin)}`}
                  />
                  {detail.interaction_sec > 0 && (
                    <CalcRow
                      icon={<Headphones size={14} />}
                      color={C.interaction}
                      label="Re-listening eliminated"
                      detail={`${formatMin(detail.interaction_sec / 60)} of interaction audio at ${relistenSpeed}× review speed`}
                      value={`+${formatMin(relistenMin)}`}
                    />
                  )}
                  {detail.narrative_sec > 0 && (
                    <CalcRow
                      icon={<Mic size={14} />}
                      color={C.narrative}
                      label="Dictation captured"
                      detail={`Officer spoke for ${formatMin(dictationMin)} — already counted as their input time`}
                      value="—"
                      muted
                    />
                  )}
                  <CalcRow
                    icon={<Clock size={14} />}
                    color="#94a3b8"
                    label="Review & edit overhead"
                    detail={`${reviewMin} min flat per case`}
                    value={`-${reviewMin} min`}
                    muted
                  />
                </div>
              </div>
            </>
          )}

          {(detail.has_enhanced_writing ||
            detail.has_ai_draft ||
            detail.has_suggestion_insert ||
            detail.has_implemented_suggestion) && (
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">
                AI assistance recorded
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.has_ai_draft && (
                  <Pill icon={<Mic size={12} />} color={C.narrative}>
                    First-draft from audio
                  </Pill>
                )}
                {detail.has_enhanced_writing && (
                  <Pill icon={<Sparkles size={12} />} color="#0891b2">
                    Enhanced writing × {detail.enhanced_writing_actions}
                  </Pill>
                )}
                {detail.has_suggestion_insert && (
                  <Pill icon={<CheckCircle2 size={12} />} color={C.good}>
                    Address Suggestions{" "}
                    {(detail.suggestion_insert_actions ?? 0) > 1
                      ? `× ${detail.suggestion_insert_actions}`
                      : ""}
                  </Pill>
                )}
                {detail.has_implemented_suggestion && (
                  <Pill icon={<CheckCircle2 size={12} />} color={C.good}>
                    Suggestions checked off ×{" "}
                    {detail.implemented_suggestions_count ?? 0}
                  </Pill>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Stat label="Words" value={detail.words.toLocaleString()} />
            <Stat
              label="Audio captured"
              value={
                detail.narrative_sec + detail.interaction_sec > 0
                  ? formatMin((detail.narrative_sec + detail.interaction_sec) / 60)
                  : "—"
              }
            />
            <Stat
              label="Quality score"
              value={detail.score != null ? String(Math.round(detail.score)) : "—"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function CalcRow({
  icon,
  color,
  label,
  detail,
  value,
  muted,
}: {
  icon: React.ReactNode;
  color: string;
  label: string;
  detail: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-md border border-slate-100">
      <span className="shrink-0 w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: color + "20", color }}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-500">{detail}</div>
      </div>
      <div className={`text-sm font-mono tabular-nums shrink-0 ${muted ? "text-slate-400" : "text-emerald-600 font-semibold"}`}>
        {value}
      </div>
    </div>
  );
}

function Pill({ icon, color, children }: { icon: React.ReactNode; color: string; children: React.ReactNode }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ backgroundColor: color + "18", color }}
    >
      {icon}
      {children}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className="text-lg font-semibold tabular-nums text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
